import type { AddItemsToActiveOrderMutation } from '#graphql-operations'
import type { AddItemToOrderVariables } from '@layers/common/app/services/activeOrder'
import { ErrorCode } from '#graphql-operations'
import { OrderModificationError } from '@layers/common/app/services/errors'
import { readSizeAdvisorShopperId, sizeAdvisorShopperIdToSet } from '~/utils/sizeAdvisorShopper'

/**
 * Fast add-to-cart shared by the PDP, the quick-add popup and the wishlist.
 *
 * Collapses the previous three sequential round-trips (refetch → mutate →
 * refresh) into a single mutation on the happy path:
 * - the pre-mutation refetch only runs while the colada cache has never been
 *   populated (the store's activeOrder query is deferred past mount, so a
 *   click within the first ~1s would otherwise mutate with no state context),
 * - a cart stranded outside AddingItems with a *stale* cache (e.g. a payment
 *   failed in another tab) surfaces as ORDER_MODIFICATION_ERROR — refetch
 *   once and retry so the store can transition it back,
 * - the legacy plugin's activeOrder (nav badges, checkout composables) is
 *   synced from the mutation result instead of a second network fetch.
 *
 * `variables` are the rest of the mutation's: the PDP passes the order line's
 * `customFields` for the size the size advisor named (`sizeAdvisorLineFields`);
 * every other add passes none, and the request carries none.
 */
export function useCartAdd() {
  const activeOrderStore = useActiveOrderStore()
  const { activeOrder: legacyActiveOrder } = useActiveOrder()
  const { toast } = useToast()
  const { enqueue } = useRequestQueue()
  // Plugin-safe: plugins/3.urlCoupon (directly and via useUrlPrize) calls this before any
  // component exists, where vue-i18n's useI18n() throws MUST_BE_CALL_SETUP_TOP.
  const { $i18n } = useNuxtApp()
  const t = (key: string, named?: Record<string, unknown>) =>
    ($i18n as { t: (key: string, named?: Record<string, unknown>) => string }).t(key, named ?? {})

  function syncLegacyActiveOrder() {
    legacyActiveOrder.value = (activeOrderStore.activeOrder.data ?? null) as typeof legacyActiveOrder.value
  }

  // The size advisor's shopper (utils/sizeAdvisorShopper.ts) onto the order, so the service can match
  // the order to its answers: once per order, when the order does not carry the id this browser's
  // widget keeps. After every add made here (PDP, quick add, wishlist), not only the widget's, since
  // the service reports on all of its shoppers' orders; not gated on the widget being configured,
  // since the id exists only where the widget ran, perhaps on an earlier visit. Through the shared
  // request queue, like the cart's shipping-country seed that follows an add: the backend saves the
  // whole order on each write, so two writes at once can undo each other. Never awaited, errors only
  // logged, and its answer goes into neither cart cache (a queued write can still overlap another
  // add, whose newer order it would replace): the next order the server sends carries the id.
  function tagSizeAdvisorShopper() {
    enqueue(async () => {
      const order = activeOrderStore.activeOrder.data
      const shopperId = order && sizeAdvisorShopperIdToSet(readSizeAdvisorShopperId(), order.customFields?.sizeAdvisorShopperId)
      if (!shopperId)
        return
      const { data, errors } = await useGraphqlMutation('setOrderCustomFields', { input: { customFields: { sizeAdvisorShopperId: shopperId } } })
      if (errors?.length || isGraphqlError(data?.setOrderCustomFields))
        console.error('Failed to set the size advisor shopper on the order', errors?.length ? errors : data?.setOrderCustomFields)
    }).catch((error) => {
      console.error('Failed to set the size advisor shopper on the order', error)
    })
  }

  async function addVariantToOrder(variantId: string | number, quantity: number, variables: AddItemToOrderVariables = {}) {
    if (activeOrderStore.activeOrder.data === undefined)
      await activeOrderStore.refetchActiveOrder()

    // The store only reads `.id` from the variant it's given.
    const variant = { id: String(variantId) } as Parameters<typeof activeOrderStore.addItemToOrder>[0]

    try {
      await activeOrderStore.addItemToOrder(variant, quantity, variables)
    }
    catch (error) {
      if (!(error instanceof OrderModificationError))
        throw error
      await activeOrderStore.refetchActiveOrder()
      await activeOrderStore.addItemToOrder(variant, quantity, variables)
    }

    syncLegacyActiveOrder()
    tagSizeAdvisorShopper()
  }

  /**
   * Several variants, once each, in ONE write (`addItemsToOrder`): a shared list's "Přidat vše do
   * košíku". One write rather than a loop of `addVariantToOrder`, because each add queues follow-up
   * writes of its own — the size advisor's tag above, the cart's shipping-country seed, the URL
   * coupon — and the next add in a loop raced them over the same order. The same guards as one add:
   * the cart read first while never read, moved back to AddingItems, and a stale state read again
   * and the write retried once — here also when Vendure refuses every input for the order's state,
   * which a batch reports in `errorResults` rather than by throwing. Answers Vendure's answer: the
   * order as it is now, and the refusals, which do not say which input they belong to.
   */
  async function addVariantsToOrder(variantIds: string[]): Promise<AddItemsToActiveOrderMutation['addItemsToOrder']> {
    if (activeOrderStore.activeOrder.data === undefined)
      await activeOrderStore.refetchActiveOrder()

    const inputs = variantIds.map(productVariantId => ({ productVariantId, quantity: 1 }))
    async function send() {
      await activeOrderStore.transitionOrderToState('AddingItems')
      const { data, errors } = await useGraphqlMutation('addItemsToActiveOrder', { inputs })
      if (!data?.addItemsToOrder?.order)
        throw new Error(errors?.[0]?.message ?? 'addItemsToOrder returned no order')
      return data.addItemsToOrder
    }

    let result: AddItemsToActiveOrderMutation['addItemsToOrder']
    try {
      result = await send()
    }
    catch (error) {
      if (!(error instanceof OrderModificationError))
        throw error
      await activeOrderStore.refetchActiveOrder()
      result = await send()
    }
    const refusedForState = result.errorResults.length === inputs.length
      && result.errorResults.every(refusal => refusal.errorCode === ErrorCode.ORDER_MODIFICATION_ERROR)
    if (refusedForState) {
      await activeOrderStore.refetchActiveOrder()
      result = await send()
    }

    activeOrderStore.publishActiveOrder(result.order)
    syncLegacyActiveOrder()
    tagSizeAdvisorShopper()
    return result
  }

  /**
   * The toast for a failed add, the same wherever it failed. Stock gets its own words; anything
   * else — a dropped connection, a refused order state — the generic line, never the exception's
   * own text, which is a request URL or the backend's English. That goes to the console instead.
   */
  function reportAddToCartError(error: unknown): 'out-of-stock' | 'failed' {
    const code = (error as { code?: string })?.code
    const quantityAvailable = (error as { quantityAvailable?: number })?.quantityAvailable

    if (code === 'INSUFFICIENT_STOCK_ERROR') {
      toast({
        variant: 'destructive',
        title: quantityAvailable && quantityAvailable > 0
          ? t('cart.add_failed.limited_stock', { count: quantityAvailable })
          : t('cart.add_failed.out_of_stock'),
      })
      return 'out-of-stock'
    }

    console.error('Failed to add to cart', error)
    toast({ variant: 'destructive', title: t('cart.add_failed.generic') })
    return 'failed'
  }

  return { addVariantToOrder, addVariantsToOrder, syncLegacyActiveOrder, reportAddToCartError }
}
